const HEVC_MIME_TYPES = [
    'video/mp4; codecs="hvc1.1.6.L93.B0"',
    'video/mp4; codecs="hev1.1.6.L93.B0"',
    'video/mp4; codecs="hvc1.1.6.L120.B0"',
];

let h265SupportCache = null;

export function browserSupportsH265() {
    if (h265SupportCache !== null) {
        return h265SupportCache;
    }

    if (typeof window === 'undefined') {
        h265SupportCache = false;
        return h265SupportCache;
    }

    if (window.MediaSource && typeof window.MediaSource.isTypeSupported === 'function') {
        h265SupportCache = HEVC_MIME_TYPES.some((mimeType) => window.MediaSource.isTypeSupported(mimeType));
        return h265SupportCache;
    }

    const video = document.createElement('video');
    h265SupportCache = HEVC_MIME_TYPES.some((mimeType) => {
        const support = video.canPlayType(mimeType);
        return support === 'probably' || support === 'maybe';
    });

    return h265SupportCache;
}

export function resolveStreamingBaseUrlWithH264Fallback(h265BaseUrl, h264BaseUrl) {
    return browserSupportsH265() ? h265BaseUrl : h264BaseUrl;
}

export function shouldOmitSeqMissionFromStreamingUrl(seriale, streamingSenzaSeqMission = []) {
    return streamingSenzaSeqMission.includes(seriale);
}

export function buildHlsPlayerStreamUrl({
    baseUrl,
    seriale,
    missionNum,
    deviceType,
    streamingSenzaSeqMission = [],
    omitMissionNum = false,
}) {
    if (!baseUrl || !seriale || !deviceType) {
        return '';
    }

    const base = String(baseUrl).replace(/\/+$/, '');
    const skipMission = omitMissionNum
        || shouldOmitSeqMissionFromStreamingUrl(seriale, streamingSenzaSeqMission)
        || deviceType === 'Drone'
        || deviceType === 'Barca';

    if (deviceType === 'Barca') {
        return 'DA-GESTIRE/index.m3u8';
    }

    if (skipMission) {
        return `${base}/${seriale}/index.m3u8`;
    }

    if (deviceType === 'ElicotteroH265' || deviceType === 'ElicotteroH264') {
        return `${base}/${seriale}/${missionNum}/index.m3u8`;
    }

    return '';
}

export function getHelicopterStreamingBaseUrl(
    seriale,
    streamingH265,
    streamingH264,
    videoStreamingH265,
    videoStreamingH264
) {
    if (streamingH265.includes(seriale)) {
        return resolveStreamingBaseUrlWithH264Fallback(videoStreamingH265, videoStreamingH264);
    }

    return videoStreamingH264;
}

export function getVideoStreamingInfo({
    seriale,
    streamingH265,
    streamingH264,
    streamingDrone,
    streamingBarca,
    videoStreamingH265,
    videoStreamingH264,
    videoStreamingDrone,
    videoStreamingBarca,
}) {
    if (streamingH265.includes(seriale)) {
        const usesH265 = browserSupportsH265();

        return {
            url: usesH265 ? videoStreamingH265 : videoStreamingH264,
            type: usesH265 ? 'ElicotteroH265' : 'ElicotteroH264',
        };
    }

    if (streamingH264.includes(seriale)) {
        return { url: videoStreamingH264, type: 'ElicotteroH264' };
    }

    if (streamingDrone.includes(seriale)) {
        return {
            url: resolveStreamingBaseUrlWithH264Fallback(videoStreamingDrone, videoStreamingH264),
            type: 'Drone',
        };
    }

    if (streamingBarca.includes(seriale)) {
        return { url: videoStreamingBarca, type: 'Barca' };
    }

    return { url: null, type: null };
}

/**
 * Link "pagina" dello stream da copiare, es.
 * https://livestreaming.hightek.it/h264/hightek/{seriale}/{missionNum}/
 */
export function buildStreamingShareUrl({
    baseUrl,
    seriale,
    missionNum,
    deviceType,
    streamingSenzaSeqMission = [],
}) {
    if (!baseUrl || !seriale) {
        return null;
    }

    const base = String(baseUrl).replace(/\/+$/, '');
    if (
        deviceType === 'Drone'
        || deviceType === 'Barca'
        || !missionNum
        || shouldOmitSeqMissionFromStreamingUrl(seriale, streamingSenzaSeqMission)
    ) {
        return `${base}/${seriale}/`;
    }

    return `${base}/${seriale}/${missionNum}/`;
}

/** Base pubblica H264 da copiare (non segue il fallback H265 del player). */
export function getStreamingShareUrl({
    seriale,
    missionNum,
    streamingH265 = [],
    streamingH264 = [],
    streamingDrone = [],
    streamingBarca = [],
    streamingSenzaSeqMission = [],
    videoStreamingH264,
    videoStreamingBarca,
}) {
    if (!seriale) {
        return null;
    }

    let baseUrl = null;
    let deviceType = null;

    // Elicotteri H265/H264 e drone: sempre prefisso H264 (mai H265).
    if (
        streamingH265.includes(seriale) ||
        streamingH264.includes(seriale) ||
        streamingDrone.includes(seriale)
    ) {
        baseUrl = videoStreamingH264;
        deviceType = streamingDrone.includes(seriale) ? 'Drone' : 'ElicotteroH264';
    } else if (streamingBarca.includes(seriale)) {
        baseUrl = videoStreamingBarca;
        deviceType = 'Barca';
    }

    return buildStreamingShareUrl({
        baseUrl,
        seriale,
        missionNum,
        deviceType,
        streamingSenzaSeqMission,
    });
}
